import type { IconProps } from '../../server';

import { clsx } from 'clsx';
import Link from 'next/link';

import { Icon } from '../../server';

type CrumbProps = {
  label: string;
  href?: string;
  icon?: IconProps;
  isLast?: boolean;
};

type BreadcrumbsProps = {
  crumbs: CrumbProps[];
};

const Crumb = ({ href, label, icon, isLast }: CrumbProps) => {
  if (isLast) {
    return (
      <span
        tabIndex={0}
        className={clsx([
          'flex flex-row items-center justify-center text-blue-700 rounded-md bg-blue-50 text-sm font-semibold py-1 gap-2',
          icon ? 'pl-1 pr-1.5' : 'px-2'
        ])}
      >
        {icon && <Icon {...icon} className={clsx(icon.className, 'h-5')} />}
        {label}
      </span>
    );
  }

  const Label = (
    <span
      className={
        'text-gray-600 group-hover:text-gray-800 group-focus:text-gray-900 text-sm font-medium'
      }
    >
      {label}
    </span>
  );

  return (
    <div
      tabIndex={0}
      className={clsx([
        'flex items-center group hover:bg-gray-100 focus:bg-gray-50 focus:shadow-[0_0_0_4px_#F2F4F7] rounded-md py-1 gap-2',
        icon ? 'pl-1 pr-1.5' : 'px-2'
      ])}
    >
      {icon && <Icon width={20} height={20} {...icon} />}
      {href ? (
        <Link tabIndex={-1} className={'group-focus:bg-gray-50'} href={href}>
          {Label}
        </Link>
      ) : (
        <>{Label}</>
      )}
    </div>
  );
};

const Breadcrumbs = ({ crumbs }: BreadcrumbsProps) => {
  const numberOfPaths = crumbs.length;

  return (
    <div className={'flex flex-row h-7'}>
      {crumbs.map((crumb, index) => (
        <div key={crumb.label} className={'flex flex-row h-7 items-center'}>
          <Crumb
            label={crumb.label}
            href={crumb.href}
            icon={crumb.icon}
            isLast={numberOfPaths === index + 1}
          />
          {numberOfPaths > 1 && numberOfPaths !== index + 1 ? (
            <Icon iconName={'iconCaretRight'} width={16} height={16} />
          ) : null}
        </div>
      ))}
    </div>
  );
};

export { Breadcrumbs };
export type { BreadcrumbsProps };
